import React from 'react';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import ECGPCalculator from '../components/ECGPCalculator';
import ReferencesAndAuthor from '../components/ReferencesAndAuthor';
import Footer from '../components/Footer';

function ECGP() {
  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900">
      <Header />
      <Breadcrumb />
      <main className="max-w-5xl mx-auto px-4 py-12">
        <span className="section-kicker">Prognóstico em TCE · pupilas</span>
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mt-2 mb-4 text-slate-800">ECG-P: Escala de Coma de Glasgow com reatividade pupilar</h1>
        <p className="text-lg text-gray-600 mb-6">A ECG-P combina o escore clássico com o Escore de Reatividade Pupilar para ampliar a discriminação prognóstica, especialmente em traumatismo cranioencefálico grave.</p>

        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-5 mb-10">
          <strong className="block text-teal-900 mb-2">Resumo</strong>
          <ul className="text-teal-900 space-y-1 text-sm">
            <li>• ECG-P = escore da ECG menos o Escore de Reatividade Pupilar (ERP).</li>
            <li>• ERP: 0 (ambas reativas) · 1 (uma não reativa) · 2 (nenhuma reativa).</li>
            <li>• Resultado varia de 1 a 15.</li>
            <li>• Foi proposta por Brennan, Murray e Teasdale em 2018.</li>
          </ul>
        </div>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-8">
          <h2 className="text-2xl font-bold mb-3">O que é a ECG-P?</h2>
          <p className="text-gray-700">A ECG-P (GCS-P) é a Escala de Coma de Glasgow combinada à reatividade pupilar. Subtrai-se do escore clássico o Escore de Reatividade Pupilar, produzindo um valor de 1 a 15. Foi proposta em 2018 para melhorar a discriminação prognóstica no traumatismo cranioencefálico, especialmente entre pacientes concentrados na faixa mais grave da escala tradicional.</p>
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm mb-8 overflow-x-auto">
          <h2 className="text-2xl font-bold mb-3">Como se calcula a ECG-P?</h2>
          <p className="text-2xl font-extrabold text-teal-700 mb-5">ECG-P = (O + V + M) − ERP</p>
          <table className="w-full border-collapse max-w-2xl">
            <thead><tr className="bg-slate-50"><th className="border p-3 text-left">ERP</th><th className="border p-3 text-left">Achado pupilar</th></tr></thead>
            <tbody>
              <tr><td className="border p-3 font-bold">0</td><td className="border p-3">Ambas as pupilas reagem à luz</td></tr>
              <tr><td className="border p-3 font-bold">1</td><td className="border p-3">Uma pupila não reage à luz</td></tr>
              <tr><td className="border p-3 font-bold">2</td><td className="border p-3">Nenhuma pupila reage à luz</td></tr>
            </tbody>
          </table>
          <p className="text-gray-700 mt-5">Exemplo: paciente com O1 V1 M2 (ECG 4) e nenhuma pupila reativa (ERP 2) resulta em <strong>ECG-P = 2</strong>.</p>
        </section>

        <ECGPCalculator />

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-3">Por que a ECG-P foi criada?</h2>
            <p className="text-gray-700">A escala clássica tem piso em 3. Ao incorporar a resposta pupilar e estender o resultado até 1, a ECG-P adiciona granularidade na faixa de maior gravidade e combina dois indicadores clínicos importantes de lesão cerebral traumática.</p>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-3">Quando usar a ECG-P?</h2>
            <p className="text-gray-700">A ECG-P foi desenvolvida para estratificação prognóstica em TCE. Para comunicação clínica de rotina, continue registrando os componentes O, V e M separadamente; o valor combinado não substitui a descrição do exame.</p>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-3">O que a ECG-P não faz?</h2>
            <p className="text-gray-700">Não substitui exame neurológico completo, neuroimagem ou avaliação de outros reflexos de tronco. Também não dispensa o registro por componentes. Um ECG-P isolado não mostra qual componente da escala está comprometido.</p>
          </section>

          <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="text-2xl font-bold mb-3">Quais fatores interferem na avaliação pupilar?</h2>
            <p className="text-gray-700">Opioides, agentes anticolinérgicos, trauma ocular direto, cirurgia oftalmológica prévia e midriáticos tópicos podem alterar a interpretação da reatividade pupilar. Documente fatores de interferência junto ao ERP.</p>
          </section>
        </div>

        <section className="bg-slate-900 text-white rounded-2xl p-6 md:p-8 mt-8">
          <h2 className="text-2xl font-bold text-white mb-4">Perguntas frequentes</h2>
          <div className="space-y-5 text-slate-100">
            <div><h3 className="font-bold text-white">Qual a pontuação mínima da ECG-P?</h3><p>1 ponto: ECG 3 com ERP 2.</p></div>
            <div><h3 className="font-bold text-white">A ECG-P substitui a Escala de Glasgow?</h3><p>Não. É uma extensão prognóstica; o registro por componentes O, V e M permanece essencial.</p></div>
            <div><h3 className="font-bold text-white">A ECG-P vale para crianças?</h3><p>Não há validação pediátrica equivalente à população adulta com TCE. Em crianças, use a <a className="underline" href="/pediatrica">Escala de Glasgow pediátrica</a> e registre reatividade pupilar como parte do exame.</p></div>
          </div>
        </section>

        <nav className="flex flex-wrap gap-4 mt-8 text-sm font-semibold" aria-label="Conteúdo relacionado">
          <a href="/#calculadora" className="text-teal-700">→ Calcular Glasgow completo</a>
          <a href="/tabela" className="text-teal-700">→ Ver tabela da ECG</a>
          <a href="/como-avaliar" className="text-teal-700">→ Como aplicar cada componente</a>
        </nav>

        <ReferencesAndAuthor />
      </main>
      <Footer />
    </div>
  );
}

export default ECGP;
